import type { CourseWithoutBlocks } from '@/types/course'
import type { LessonUpdate } from '@/types/lesson'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { useDeleteLessonMutation, useUpdateLessonMutation } from '@/features/editor/lessonsApi'
import { selectLesson } from '@/features/editor/editorSlice'
import { refusedByReview } from '@/features/editor/lib/reviewGuard'
import { useSaveStatusWrap } from '@/features/editor/hooks/useSaveStatusWrap'
import { buildLessonUpdate } from '@/features/editor/lib/buildLessonUpdate'
import { getLocalizedField, setLocalizedField } from '@/features/editor/lib/localizedField'
import { readPrompt, writePrompt } from '@/features/editor/lib/imagePrompt'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

// The selected lesson (when no block is selected) + a name/description save for
// the current content language. Full-body PUT via buildLessonUpdate.
export const useLessonPanel = (course: CourseWithoutBlocks) => {
  const selectedLessonId = useAppSelector((s) => s.editor.selectedLessonId)
  const contentLang = useAppSelector((s) => s.editor.contentLang)
  const [updateLesson] = useUpdateLessonMutation()
  const [deleteLesson] = useDeleteLessonMutation()
  const dispatch = useAppDispatch()
  const wrap = useSaveStatusWrap()

  const idx = course.lessons.findIndex((l) => l.id === selectedLessonId)
  const lesson = idx >= 0 ? course.lessons[idx] : null

  const savePatch = (patch: LessonUpdate): Promise<boolean> => {
    if (!lesson) return Promise.resolve(false)
    return wrap(
      updateLesson({
        id: lesson.id,
        courseId: course.id,
        patch: buildLessonUpdate(lesson, patch),
      }).unwrap(),
      messagesNow(messages).saveLessonFailed,
    )
  }

  const save = (name: string, description: string) => {
    void savePatch({
      ...setLocalizedField('name', contentLang, name),
      ...setLocalizedField('description', contentLang, description),
    })
  }

  // Удаление урока из его панели. Выбор снимаем ДО запроса — так же, как
  // делала корзинка ленты (`useCourseTimeline`): панель не должна остаться
  // открытой над уроком, которого уже нет.
  const remove = async (isFrozen: boolean): Promise<boolean> => {
    if (!lesson || refusedByReview(isFrozen)) return false
    dispatch(selectLesson(null))
    return wrap(
      deleteLesson({ id: lesson.id, courseId: course.id }).unwrap(),
      messagesNow(messages).deleteLessonFailed,
    )
  }

  return {
    lesson,
    remove,
    lessonNumber: idx + 1,
    contentLang,
    name: lesson ? (getLocalizedField(lesson, 'name', contentLang) ?? '') : '',
    description: lesson ? (getLocalizedField(lesson, 'description', contentLang) ?? '') : '',
    image: lesson ? getLocalizedField(lesson, 'image', contentLang) : null,
    imagePrompt: lesson ? readPrompt(lesson, contentLang) : '',
    // `null` бэк в PUT урока ИГНОРИРУЕТ (§5.2) — снять картинку можно только
    // пустой строкой, ровно как у баннера курса.
    saveImage: (url: string | null) =>
      savePatch(setLocalizedField('image', contentLang, url ?? '')),
    savePrompt: (text: string) => savePatch(writePrompt(contentLang, text)),
    // Наружу — ради секции оформления: тема урока не локализована, и своего
    // именованного сеттера ей заводить незачем.
    savePatch,
    save,
  }
}
